import { Injectable } from '@angular/core';

export interface TemplateColumnConfig {
  name: string;
  required?: boolean;
  description?: string;
}

export interface ModuleTemplateConfig {
  module: string;
  columns: TemplateColumnConfig[];
  defaultColumns?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  
  // Default column configurations for each module
  private moduleTemplates: Record<string, TemplateColumnConfig[]> = {
    'vendor': [
      { name: 'company_name', required: true },
      { name: 'type', required: false },
      { name: 'category', required: false },
      { name: 'vendor_name', required: false },
      { name: 'vendor_type', required: false },
      { name: 'vendor_group', required: false },
      { name: 'vendor_email', required: false },
      { name: 'vendor_phone', required: false },
      { name: 'alt_phone', required: false },
      { name: 'pan_number', required: false },
      { name: 'tax_identification_number', required: false },
      { name: 'tan_number', required: false },
      { name: 'registration_type', required: false },
      { name: 'registration_number', required: false },
      { name: 'msme', required: false },
      { name: 'msme_number', required: false },
      { name: 'msme_category', required: false },
      { name: 'legal_name', required: false },
      { name: 'default_payable_account', required: false },
      { name: 'default_expense_account', required: false },
      { name: 'terms', required: false },
      { name: 'credit_limit', required: false },
      { name: 'bank_account_number', required: false },
      { name: 'bank_name', required: false },
      { name: 'bank_account_type', required: false },
      { name: 'bank_ifsc_code', required: false },
      { name: 'bank_branch', required: false },
      { name: 'bank_state', required: false },
      { name: 'bank_country', required: false },
      { name: 'gst_number', required: false },
      { name: 'address', required: false },
      { name: 'pin', required: false },
      { name: 'city', required: false },
      { name: 'state', required: false },
      { name: 'country', required: false },
      { name: 'address_shipping', required: false },
      { name: 'pin_shipping', required: false },
      { name: 'city_shipping', required: false },
      { name: 'state_shipping', required: false },
      { name: 'country_shipping', required: false },
      { name: 'opening_balance', required: false },
      { name: 'opening_balance_date', required: false },
      { name: 'opening_balance_account', required: false },
      { name: 'contact', required: false },
      { name: 'job_title', required: false },
      { name: 'email', required: false },
      { name: 'phone', required: false },
      { name: 'subsidiary', required: false }
    ],
    'vendor-po': [
      { name: 'PO Number', required: true },
      { name: 'Vendor Name', required: true },
      { name: 'PO Date', required: true },
      { name: 'Expected Delivery Date', required: false },
      { name: 'Item Name', required: true },
      { name: 'Quantity', required: true },
      { name: 'Unit Price', required: true },
      { name: 'Total Amount', required: false },
      { name: 'Tax Amount', required: false }
    ],
    'vendor-invoice': [
      { name: 'vendor_invoice_no', required: true },
      { name: 'vendor_code', required: false },
      { name: 'vendor_name', required: true },
      { name: 'invoice_date', required: true },
      { name: 'due_date', required: false },
      { name: 'po_number', required: false },
      { name: 'subsidiary', required: false },
      { name: 'location', required: false },
      { name: 'ship_to', required: false },
      { name: 'invoice_type', required: false },
      { name: 'account', required: false },
      { name: 'gross_amount', required: false },
      { name: 'discount_amount', required: false },
      { name: 'tax_amount', required: false },
      { name: 'tds_amount', required: false },
      { name: 'final_amount', required: false },
      { name: 'remarks', required: false },
      { name: 'memo', required: false },
      { name: 'is_draft', required: false },
      { name: 'approval_status', required: false },
      { name: 'accounting_status', required: false },
      { name: 'default_payable_account', required: false },
      { name: 'payment_terms', required: false },
      { name: 'subsidiary_gst_number', required: false },
      { name: 'vendor_gst_number', required: false },
      { name: 'registration_type', required: false },
      { name: 'place_of_supply', required: false },
      { name: 'gst_exempt', required: false },
      { name: 'shipping_date', required: false },
      { name: 'item_code', required: false },
      { name: 'item_name', required: false },
      { name: 'item_description', required: false },
      { name: 'item_quantity', required: false },
      { name: 'item_rate', required: false },
      { name: 'item_mrp', required: false },
      { name: 'item_gross_amount', required: false },
      { name: 'item_discount_percentage', required: false },
      { name: 'item_discount_amount', required: false },
      { name: 'item_tax_rate', required: false },
      { name: 'item_tax_type', required: false },
      { name: 'item_tax_amount', required: false },
      { name: 'item_tds_rate', required: false },
      { name: 'item_tds_amount', required: false },
      { name: 'item_total_amount', required: false },
      { name: 'item_hsn_code', required: false },
      { name: 'item_uqc', required: false },
      { name: 'item_serial_no', required: false },
      { name: 'item_expiration_date', required: false },
      { name: 'expense_account_category', required: false },
      { name: 'expense_account', required: false },
      { name: 'expense_amount', required: false },
      { name: 'expense_discount_percentage', required: false },
      { name: 'expense_discount_amount', required: false },
      { name: 'expense_tax_rate', required: false },
      { name: 'expense_tax_type', required: false },
      { name: 'expense_tax_amount', required: false },
      { name: 'expense_tds_rate', required: false },
      { name: 'expense_tds_section', required: false },
      { name: 'expense_tds_amount', required: false },
      { name: 'expense_gross_amount', required: false },
      { name: 'expense_total_amount', required: false },
      { name: 'expense_description', required: false }
    ],
    'grn': [
      { name: 'GRN Number', required: true },
      { name: 'PO Number', required: true },
      { name: 'Vendor Name', required: true },
      { name: 'GRN Date', required: true },
      { name: 'Item Name', required: true },
      { name: 'Received Quantity', required: true },
      { name: 'Accepted Quantity', required: false },
      { name: 'Rejected Quantity', required: false }
    ],
    'vendor-payment': [
      { name: 'Payment Number', required: true },
      { name: 'Vendor Name', required: true },
      { name: 'Payment Date', required: true },
      { name: 'Payment Amount', required: true },
      { name: 'Payment Method', required: false },
      { name: 'Reference Number', required: false },
      { name: 'Invoice Number', required: false }
    ],
    'vendor-return': [
      { name: 'Return Number', required: true },
      { name: 'Vendor Name', required: true },
      { name: 'Return Date', required: true },
      { name: 'Item Name', required: true },
      { name: 'Return Quantity', required: true },
      { name: 'Return Reason', required: false }
    ],
    'vendor-debit-note': [
      { name: 'Debit Note Number', required: true },
      { name: 'Vendor Name', required: true },
      { name: 'Debit Note Date', required: true },
      { name: 'Amount', required: true },
      { name: 'Reason', required: false },
      { name: 'Reference Invoice', required: false }
    ],
    'client': [
      { name: 'client_name', required: true },
      { name: 'client_type', required: false },
      { name: 'client_category_name', required: false },
      { name: 'gender', required: false },
      { name: 'default_receivable_account_name', required: false },
      { name: 'asset_account_name', required: false },
      { name: 'opening_balance', required: false },
      { name: 'opening_balance_date', required: false },
      { name: 'bank_account_number', required: false },
      { name: 'bank_account_type', required: false },
      { name: 'bank_account_ifsccode', required: false },
      { name: 'bank_account_branch', required: false },
      { name: 'bank_account_city', required: false },
      { name: 'bank_account_state', required: false },
      { name: 'bank_account_country', required: false },
      { name: 'client_group_name', required: false },
      { name: 'client_email', required: false },
      { name: 'client_phone', required: false },
      { name: 'pan_number', required: false },
      { name: 'registration_type', required: false },
      { name: 'gst_number', required: false },
      { name: 'address', required: false },
      { name: 'pin', required: false },
      { name: 'city_name', required: false },
      { name: 'state_name', required: false },
      { name: 'country_name', required: false },
      { name: 'shipping_address', required: false },
      { name: 'shipping_pin', required: false },
      { name: 'shipping_city_name', required: false },
      { name: 'shipping_state_name', required: false },
      { name: 'shipping_country_name', required: false },
      { name: 'subsidiary_name', required: false },
      { name: 'credit_limit', required: false }
    ],
    'client-order': [
      { name: 'Order Number', required: true },
      { name: 'Client Name', required: true },
      { name: 'Order Date', required: true },
      { name: 'Expected Delivery Date', required: false },
      { name: 'Item Name', required: true },
      { name: 'Quantity', required: true },
      { name: 'Unit Price', required: true },
      { name: 'Total Amount', required: false }
    ],
    'client-invoice': [
      { name: 'Invoice Number', required: true },
      { name: 'Client Name', required: true },
      { name: 'Invoice Date', required: true },
      { name: 'Due Date', required: false },
      { name: 'Item Name', required: true },
      { name: 'Quantity', required: true },
      { name: 'Unit Price', required: true },
      { name: 'Total Amount', required: false },
      { name: 'Tax Amount', required: false }
    ],
    'client-return': [
      { name: 'Return Number', required: true },
      { name: 'Client Name', required: true },
      { name: 'Return Date', required: true },
      { name: 'Item Name', required: true },
      { name: 'Return Quantity', required: true },
      { name: 'Return Reason', required: false }
    ],
    'credit-note': [
      { name: 'Credit Note Number', required: true },
      { name: 'Client Name', required: true },
      { name: 'Credit Note Date', required: true },
      { name: 'Amount', required: true },
      { name: 'Reason', required: false },
      { name: 'Reference Invoice', required: false }
    ],
    'client-payment': [
      { name: 'Payment Number', required: true },
      { name: 'Client Name', required: true },
      { name: 'Payment Date', required: true },
      { name: 'Payment Amount', required: true },
      { name: 'Payment Method', required: false },
      { name: 'Reference Number', required: false },
      { name: 'Invoice Number', required: false }
    ],
    'item': [
      { name: 'name', required: true },
      { name: 'product_code', required: false },
      { name: 'w', required: false },
      { name: 'category_name', required: false },
      { name: 'nature', required: false },
      { name: 'preferred_vendor_name', required: false },
      { name: 'manufacturer_name', required: false },
      { name: 'hsn_code', required: false },
      { name: 'item_weight', required: false },
      { name: 'weight_measure', required: false },
      { name: 'income_account_name', required: false },
      { name: 'asset_account_name', required: false },
      { name: 'cogs_account_name', required: false },
      { name: 'sku', required: false },
      { name: 'type_name', required: false },
      { name: 'packaging_name', required: false },
      { name: 'composition', required: false },
      { name: 'gst_percentage', required: false },
      { name: 'packing_unit', required: false },
      { name: 'lead_time_value', required: false },
      { name: 'storage_option_name', required: false },
      { name: 'expiry_category_name', required: false },
      { name: 'preferred_stock_level', required: false },
      { name: 'preferred_stock_quantity', required: false },
      { name: 'preferred_stock_day', required: false },
      { name: 'reorder_point_alert', required: false },
      { name: 'transfer_price', required: false },
      { name: 'safety_stock', required: false },
      { name: 'safety_stock_days', required: false },
      { name: 'expected_damage_change', required: false },
      { name: 'minimum_quantity', required: false },
      { name: 'maximum_quantity', required: false },
      { name: 'mrp', required: false },
      { name: 'discount_percent', required: false }
    ],
    'inventory': [
      { name: 'Item Name', required: true },
      { name: 'Item Code', required: false },
      { name: 'Location', required: false },
      { name: 'Quantity', required: true },
      { name: 'Unit', required: false },
      { name: 'Batch Number', required: false },
      { name: 'Expiry Date', required: false }
    ]
  };

  /**
   * Get default columns for a module
   */
  getDefaultColumns(module: string): TemplateColumnConfig[] {
    return this.moduleTemplates[module] || [];
  }

  /**
   * Download CSV template for a module
   * @param module - Module name
   * @param columns - Array of selected column names (if empty, uses all default columns)
   */
  downloadTemplate(module: string, columns: string[] = []): void {
    const moduleColumns = this.getDefaultColumns(module);
    
    // Use provided columns or default columns
    let headers: string[];
    
    if (columns.length > 0) {
      // Use only the selected columns, maintaining their order
      headers = columns.filter(colName => 
        moduleColumns.some(col => col.name === colName)
      );
    } else {
      // Use all default columns if none specified
      headers = moduleColumns.map(col => col.name);
    }

    if (headers.length === 0) {
      console.warn('No columns available for download');
      return;
    }

    // Escape CSV values (wrap in quotes if contains comma, quote, or newline)
    const escapeCsvValue = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Create CSV content with headers only (template)
    const escapedHeaders = headers.map(escapeCsvValue);
    const csvContent = escapedHeaders.join(',');

    // BOM for Excel UTF-8 support
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${module}_template_${new Date().toISOString().slice(0, 10)}.csv`;
    link.style.display = 'none';
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    
    // Clean up after a short delay to ensure download starts
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  }

  /**
   * Update module template configuration
   */
  updateModuleTemplate(module: string, columns: TemplateColumnConfig[]): void {
    this.moduleTemplates[module] = columns;
  }
}

